import { useEffect, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import api, { API, formatApiError } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { ArrowLeft, Plus, PencilSimple, Copy, Trash, Eye, UploadSimple } from "@phosphor-icons/react";

const CTA_OPTIONS = [
  { key: "trucks_near", label: "Food trucks near this area (live data)" },
  { key: "upcoming_locations", label: "Upcoming truck locations (live data)" },
  { key: "cta_map", label: "CTA: Find food trucks near you" },
  { key: "cta_request_truck", label: "CTA: Request a food truck" },
];

const EMPTY = {
  title: "", slug: "", excerpt: "", content: "", cover_image_path: null,
  cover_image_alt: "", category: "Guides", target_city: "", target_state: "",
  author: "Jason Lewis", seo_title: "", meta_description: "", primary_keyword: "",
  secondary_keywords: [], cta_sections: [], status: "draft", published_at: null,
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{label}</span>
      {children}
      {hint && <span className="block mt-1 text-[11px] text-muted-foreground">{hint}</span>}
    </label>
  );
}

function Editor({ post, cats, onBack, onSaved, onAddCategory }) {
  const [extraCat, setExtraCat] = useState(null);
  const [p, setP] = useState({ ...EMPTY, ...post, secondary_keywords: post?.secondary_keywords || [] });
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const bodyRef = useRef(null);
  const set = (k, v) => setP((x) => ({ ...x, [k]: v }));

  const insert = (before, after = "") => {
    const el = bodyRef.current;
    if (!el) return;
    const { selectionStart: s, selectionEnd: e, value } = el;
    const sel = value.slice(s, e) || "text";
    set("content", value.slice(0, s) + before + sel + after + value.slice(e));
  };

  const upload = async (file, asCover) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/blog/admin/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (asCover) set("cover_image_path", data.path);
      else insert(`\n![describe this image](${API}/files/${data.path})\n`);
      toast.success("Image uploaded");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const importDoc = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/blog/admin/import-doc", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const f = data.fields || {};
      setP((x) => ({
        ...x,
        content: x.content?.trim() ? `${x.content}\n\n${data.content}` : data.content,
        title: x.title?.trim() ? x.title : data.title,
        slug: x.slug?.trim() ? x.slug : (f.slug || x.slug),
        seo_title: x.seo_title?.trim() ? x.seo_title : (f.seo_title || x.seo_title),
        meta_description: x.meta_description?.trim() ? x.meta_description : (f.meta_description || x.meta_description),
        primary_keyword: x.primary_keyword?.trim() ? x.primary_keyword : (f.primary_keyword || x.primary_keyword),
        secondary_keywords: x.secondary_keywords?.length ? x.secondary_keywords : (f.secondary_keywords || []),
        category: f.category || x.category,
        cover_image_alt: x.cover_image_alt?.trim() ? x.cover_image_alt : (f.cover_image_alt || x.cover_image_alt),
        cta_sections: x.cta_sections?.length ? x.cta_sections : (f.cta_sections || []),
        excerpt: x.excerpt?.trim() ? x.excerpt : (f.excerpt || x.excerpt),
      }));
      if (f.category && !cats.some((c) => c.name === f.category)) {
        try {
          await api.post("/blog/admin/categories", { name: f.category });
          setExtraCat(f.category);
        } catch { /* category may already exist */ }
      }
      toast.success(data.fields ? "Article + SEO fields imported — review, add a cover image, then publish" : "Article imported — review the formatting, then publish");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const save = async (status) => {
    if (!p.title.trim()) return toast.error("Title is required");
    setSaving(true);
    const payload = { ...p, status: status || p.status, target_city: p.target_city || null, target_state: p.target_state || null };
    try {
      const { data } = p.id
        ? await api.put(`/blog/admin/posts/${p.id}`, payload)
        : await api.post("/blog/admin/posts", payload);
      setP((x) => ({ ...x, ...data }));
      toast.success(data.status === "published" ? "Published!" : "Draft saved");
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    setSaving(false);
  };

  return (
    <div data-testid="blog-editor">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="blog-editor-back" className="gap-1">
          <ArrowLeft className="h-4 w-4" /> All articles
        </Button>
        <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ${p.status === "published" ? "bg-emerald-500/15 text-emerald-600" : "bg-amber-500/15 text-amber-600"}`}>
          {p.status}
        </span>
        <div className="ml-auto flex gap-2">
          {p.status === "published" && p.slug && (
            <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer">
              <Button variant="outline" size="sm" className="gap-1"><Eye className="h-4 w-4" /> View live</Button>
            </a>
          )}
          <Button variant="outline" size="sm" onClick={() => save("draft")} disabled={saving} data-testid="blog-save-draft">
            Save draft
          </Button>
          {p.status === "published" ? (
            <Button variant="outline" size="sm" onClick={() => save("draft")} disabled={saving} data-testid="blog-unpublish">Unpublish</Button>
          ) : null}
          <Button size="sm" onClick={() => save("published")} disabled={saving} className="bg-primary text-primary-foreground" data-testid="blog-publish">
            {p.status === "published" ? "Update" : "Publish"}
          </Button>
        </div>
      </div>

      <div className="mt-5 grid lg:grid-cols-[1fr_320px] gap-6">
        <div className="space-y-4">
          <Field label="Title">
            <Input value={p.title} onChange={(e) => set("title", e.target.value)} placeholder="How to hire a food truck for an event" data-testid="blog-title-input" className="mt-1" />
          </Field>
          <Field label="URL slug" hint={p.id ? "Changing this after publish creates a permanent redirect from the old URL." : "Leave blank to auto-generate from the title."}>
            <Input value={p.slug || ""} onChange={(e) => set("slug", e.target.value)} placeholder="how-to-hire-a-food-truck" data-testid="blog-slug-input" className="mt-1 font-mono text-sm" />
          </Field>
          <Field label="Excerpt" hint="Shown on the blog cards and used as the default meta description.">
            <Textarea value={p.excerpt} onChange={(e) => set("excerpt", e.target.value)} rows={2} className="mt-1" data-testid="blog-excerpt-input" />
          </Field>

          <div>
            <div className="flex flex-wrap items-center gap-1.5 mb-1">
              {[["**", "**", "B"], ["*", "*", "I"], ["\n## ", "", "H2"], ["\n### ", "", "H3"], ["\n- ", "", "• List"], ["\n1. ", "", "1. List"], ["[", "](https://)", "Link"], ["\n> ", "", "Quote"]].map(([b, a, lbl]) => (
                <button key={lbl} type="button" onClick={() => insert(b, a)}
                  className="h-7 px-2 rounded border border-border bg-card text-xs font-semibold hover:bg-muted">
                  {lbl}
                </button>
              ))}
              <label className="h-7 px-2 rounded border border-border bg-card text-xs font-semibold hover:bg-muted cursor-pointer inline-flex items-center gap-1">
                <UploadSimple className="h-3.5 w-3.5" /> Image
                <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0], false)} />
              </label>
              <label className="h-7 px-2 rounded border border-primary/40 bg-primary/10 text-primary text-xs font-semibold hover:bg-primary/20 cursor-pointer inline-flex items-center gap-1" data-testid="blog-import-doc">
                <UploadSimple className="h-3.5 w-3.5" /> Import .docx
                <input type="file" accept=".docx,.md,.markdown,.txt" className="hidden"
                  onChange={(e) => { if (e.target.files[0]) { importDoc(e.target.files[0]); e.target.value = ""; } }} />
              </label>
              <button type="button" onClick={() => setPreview(!preview)}
                className={`ml-auto h-7 px-3 rounded border text-xs font-semibold ${preview ? "bg-primary text-primary-foreground border-primary" : "border-border bg-card hover:bg-muted"}`}
                data-testid="blog-preview-toggle">
                {preview ? "Edit" : "Preview"}
              </button>
            </div>
            {preview ? (
              <div className="rounded-xl border border-border bg-card p-5 prose prose-neutral dark:prose-invert max-w-none text-sm [&_h2]:text-xl [&_h3]:text-lg" data-testid="blog-preview-pane">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{p.content || "*Nothing to preview yet.*"}</ReactMarkdown>
              </div>
            ) : (
              <Textarea ref={bodyRef} value={p.content} onChange={(e) => set("content", e.target.value)} rows={20}
                placeholder={"Write in Markdown…\n\n## A heading\n\nSome **bold** text, a [link](https://thetrucktrackr.com/map), and:\n\n- a list item"}
                className="font-mono text-sm" data-testid="blog-content-input" />
            )}
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-xl border border-border bg-card p-4 space-y-3">
            <Field label="Category">
              <div className="mt-1 flex gap-2">
                <select value={p.category} onChange={(e) => set("category", e.target.value)}
                  className="flex-1 h-9 rounded-md border border-border bg-background px-2 text-sm" data-testid="blog-category-select">
                  {cats.map((c) => <option key={c.slug} value={c.name}>{c.name}</option>)}
                  {extraCat && !cats.some((c) => c.name === extraCat) && <option value={extraCat}>{extraCat}</option>}
                </select>
                <Button variant="outline" size="sm" onClick={onAddCategory} title="Add category">+</Button>
              </div>
            </Field>
            <Field label="Target city (optional)">
              <Input value={p.target_city || ""} onChange={(e) => set("target_city", e.target.value)} placeholder="Lancaster" className="mt-1" data-testid="blog-city-input" />
            </Field>
            <Field label="Target state (optional)">
              <Input value={p.target_state || ""} onChange={(e) => set("target_state", e.target.value)} placeholder="PA" className="mt-1" />
            </Field>
            <Field label="Author">
              <Input value={p.author} onChange={(e) => set("author", e.target.value)} className="mt-1" />
            </Field>
          </div>

          <div className="rounded-xl border border-border bg-card p-4 space-y-3">
            <div className="text-xs font-bold uppercase tracking-wider text-primary">Cover image</div>
            {p.cover_image_path && (
              <img src={`${API}/files/${p.cover_image_path}`} alt="cover" className="w-full h-28 object-cover rounded-lg border border-border" />
            )}
            <label className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-border bg-background text-sm font-semibold cursor-pointer hover:bg-muted">
              <UploadSimple className="h-4 w-4" /> {p.cover_image_path ? "Change image" : "Upload image"}
              <input type="file" accept="image/*" className="hidden" data-testid="blog-cover-upload"
                onChange={(e) => e.target.files[0] && upload(e.target.files[0], true)} />
            </label>
            <Field label="Image alt text">
              <Input value={p.cover_image_alt} onChange={(e) => set("cover_image_alt", e.target.value)} placeholder="Describe the image for SEO" className="mt-1" />
            </Field>
          </div>

          <div className="rounded-xl border border-border bg-card p-4 space-y-3">
            <div className="text-xs font-bold uppercase tracking-wider text-primary">SEO</div>
            <Field label="SEO title" hint="Defaults to the article title.">
              <Input value={p.seo_title || ""} onChange={(e) => set("seo_title", e.target.value)} className="mt-1" data-testid="blog-seo-title-input" />
            </Field>
            <Field label="Meta description">
              <Textarea value={p.meta_description || ""} onChange={(e) => set("meta_description", e.target.value)} rows={2} className="mt-1" data-testid="blog-meta-desc-input" />
            </Field>
            <Field label="Primary keyword">
              <Input value={p.primary_keyword || ""} onChange={(e) => set("primary_keyword", e.target.value)} className="mt-1" />
            </Field>
            <Field label="Secondary keywords (comma separated)">
              <Input value={(p.secondary_keywords || []).join(", ")}
                onChange={(e) => set("secondary_keywords", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} className="mt-1" />
            </Field>
          </div>

          <div className="rounded-xl border border-border bg-card p-4 space-y-2">
            <div className="text-xs font-bold uppercase tracking-wider text-primary">Dynamic sections</div>
            {CTA_OPTIONS.map((o) => (
              <label key={o.key} className="flex items-start gap-2 text-sm cursor-pointer">
                <input type="checkbox" className="mt-0.5" checked={(p.cta_sections || []).includes(o.key)}
                  data-testid={`blog-cta-${o.key}`}
                  onChange={(e) => set("cta_sections", e.target.checked
                    ? [...(p.cta_sections || []), o.key]
                    : (p.cta_sections || []).filter((k) => k !== o.key))} />
                {o.label}
              </label>
            ))}
            <p className="text-[11px] text-muted-foreground">Live-data sections need a target city or state.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminBlog() {
  const { user, loading: authLoading } = useAuth();
  const [posts, setPosts] = useState([]);
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null); // null | {} (new) | post
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const load = () => {
    api.get("/blog/admin/posts", { params: { q: q || undefined, status: statusFilter || undefined } })
      .then(({ data }) => setPosts(data)).catch(() => {});
  };
  useEffect(() => { if (user?.is_admin) load(); }, [user, q, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { api.get("/blog/categories").then(({ data }) => setCats(data)).catch(() => {}); }, []);

  if (authLoading) return null;
  if (!user?.is_admin) return <Navigate to="/" replace />;

  const openEdit = async (post) => {
    const { data } = await api.get(`/blog/admin/posts/${post.id}`);
    setEditing(data);
  };
  const duplicate = async (post) => {
    try { await api.post(`/blog/admin/posts/${post.id}/duplicate`); toast.success("Duplicated as draft"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const remove = async (post) => {
    if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return;
    try { await api.delete(`/blog/admin/posts/${post.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const addCategory = async () => {
    const name = window.prompt("New category name:");
    if (!name) return;
    try {
      await api.post("/blog/admin/categories", { name });
      const { data } = await api.get("/blog/categories");
      setCats(data);
      toast.success("Category added");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10" data-testid="admin-blog-page">
      {editing !== null ? (
        <Editor post={editing} cats={cats} onBack={() => { setEditing(null); load(); }} onSaved={load} onAddCategory={addCategory} />
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <div>
              <Link to="/admin" className="text-xs text-muted-foreground hover:text-foreground">← Admin dashboard</Link>
              <h1 className="font-display font-bold tracking-tight text-3xl">Blog Manager</h1>
            </div>
            <Button className="ml-auto gap-1.5 bg-primary text-primary-foreground rounded-full" onClick={() => setEditing({})} data-testid="blog-new-post-btn">
              <Plus className="h-4 w-4" /> New article
            </Button>
          </div>

          <div className="mt-5 flex flex-wrap gap-2">
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search titles…" className="max-w-xs" data-testid="blog-search-input" />
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
              className="h-9 rounded-md border border-border bg-background px-2 text-sm" data-testid="blog-status-filter">
              <option value="">All statuses</option>
              <option value="published">Published</option>
              <option value="draft">Drafts</option>
            </select>
          </div>

          <div className="mt-4 rounded-xl border border-border overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-2.5">Title</th>
                  <th className="px-4 py-2.5">Category</th>
                  <th className="px-4 py-2.5">Location</th>
                  <th className="px-4 py-2.5">Status</th>
                  <th className="px-4 py-2.5">Published</th>
                  <th className="px-4 py-2.5">Updated</th>
                  <th className="px-4 py-2.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {posts.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">No articles yet — write your first one.</td></tr>
                )}
                {posts.map((p) => (
                  <tr key={p.id} className="border-t border-border" data-testid="blog-admin-row">
                    <td className="px-4 py-3 font-semibold max-w-[280px] truncate">{p.title}</td>
                    <td className="px-4 py-3">{p.category}</td>
                    <td className="px-4 py-3 text-muted-foreground">{p.target_city ? `${p.target_city}${p.target_state ? ", " + p.target_state : ""}` : (p.target_state || "—")}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] font-bold uppercase rounded-full px-2 py-0.5 ${p.status === "published" ? "bg-emerald-500/15 text-emerald-600" : "bg-amber-500/15 text-amber-600"}`}>{p.status}</span>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{fmt(p.published_at)}</td>
                    <td className="px-4 py-3 text-muted-foreground">{fmt(p.updated_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(p)} data-testid="blog-edit-btn" title="Edit"><PencilSimple className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => duplicate(p)} title="Duplicate"><Copy className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => remove(p)} title="Delete" className="text-destructive"><Trash className="h-4 w-4" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
